'use client';

/**
 * CallBotProblem — the "imagine this" section.
 *
 * Two-column "without/with" framing lifted from the docs (Part 10
 * Chapter 29.1). Concrete: a customer at 10 PM Saturday with three
 * questions. Without CallBot they wait, forget, or churn; with
 * CallBot they get answered now.
 */

import React from 'react';
import { Locale } from '../../../../i18n-config';
import SectionLabel from '../home/SectionLabel';
import { useTranslation } from '@/i18n/client';
import { HiCheckCircle, HiXCircle } from 'react-icons/hi2';

type Props = { lang: Locale };

const CallBotProblem = ({ lang }: Props) => {
  const { t } = useTranslation(lang);
  const questions = t('CallBot/Problem/questions', { returnObjects: true }) as string[];
  const withoutItems = t('CallBot/Problem/without', { returnObjects: true }) as string[];
  const withItems = t('CallBot/Problem/with', { returnObjects: true }) as string[];

  return (
    <section
      id="problem"
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0A0E27',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="01" label={t('CallBot/Problem/badge')} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {t('CallBot/Problem/title1')} <span style={{ color: '#B4C1D8' }}>{t('CallBot/Problem/title2')}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 48px',
          }}
        >
          {t('CallBot/Problem/subtitle')}
        </p>

        <div className="flex flex-col gap-5">
          <div
            className="rounded-2xl p-7"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <div
              className="mono"
              style={{
                fontSize: 11,
                letterSpacing: 1.5,
                color: '#B4C1D8',
                textTransform: 'uppercase',
                marginBottom: 12,
              }}
            >
              {t('CallBot/Problem/questions_label')}
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {questions.map((q, i) => (
                <li
                  key={i}
                  style={{
                    color: '#F5F7FA',
                    fontSize: 17,
                    fontStyle: 'italic',
                    lineHeight: 1.5,
                    padding: '10px 0',
                    borderTop: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  {q}
                </li>
              ))}
            </ul>
          </div>

          <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div
              className="rounded-2xl p-5"
              style={{
                background: 'rgba(255,77,77,0.06)',
                border: '1px solid rgba(255,77,77,0.30)',
              }}
            >
              <div
                className="mono flex items-center gap-2"
                style={{
                  fontSize: 11,
                  letterSpacing: 1.5,
                  color: '#FF4D4D',
                  textTransform: 'uppercase',
                  marginBottom: 12,
                }}
              >
                <HiXCircle style={{ width: 14, height: 14 }} />
                {t('CallBot/Problem/without_label')}
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {withoutItems.map((item, i) => (
                  <li
                    key={i}
                    style={{
                      color: '#B4C1D8',
                      fontSize: 13.5,
                      lineHeight: 1.5,
                      padding: '6px 0',
                    }}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div
              className="rounded-2xl p-5"
              style={{
                background: 'rgba(167,139,250,0.06)',
                border: '1px solid rgba(167,139,250,0.30)',
              }}
            >
              <div
                className="mono flex items-center gap-2"
                style={{
                  fontSize: 11,
                  letterSpacing: 1.5,
                  color: '#A78BFA',
                  textTransform: 'uppercase',
                  marginBottom: 12,
                }}
              >
                <HiCheckCircle style={{ width: 14, height: 14 }} />
                {t('CallBot/Problem/with_label')}
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {withItems.map((item, i) => (
                  <li
                    key={i}
                    style={{
                      color: '#B4C1D8',
                      fontSize: 13.5,
                      lineHeight: 1.5,
                      padding: '6px 0',
                    }}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CallBotProblem;
